// VI-623 — shared date-range → { startDate, endDate } resolver.
//
// Extracted from InterviewsPage's filterByDateRange so the client-side grid
// filter and the AS-8108 submission-counts request use IDENTICAL bounds. This
// keeps the "Responses (X)" tab / "Showing N of Y responses" totals aligned
// with what the grid actually displays for the selected DatePicker range.
//
// Supported ranges match the DatePicker options: 'Today', 'Yesterday',
// 'Last 7 Days', 'This Month', 'Last Month', 'Last 3 Months', 'Last 6 Months',
// 'This Year', 'Last 30 Days' (default), 'All', and custom
// "MM/DD/YYYY - MM/DD/YYYY" strings.

export interface DateRangeBounds {
  startDate: Date;
  endDate: Date;
}

// The counts endpoint requires a bounded range, so 'All' resolves to a very
// wide window (epoch → now) rather than being unbounded.
const EPOCH_START = new Date(0);

// VI-711: the date-picker label is rendered from LOCAL calendar parts
// (e.g. "07/02/2026"). Each case below computes the correct calendar day in
// local time; we then pin that day's y/m/d to UTC so `.toISOString()` (used by
// the counts request) preserves the date the user actually sees. Without this,
// local-midnight → UTC shifts the day for viewers east of UTC (e.g. "Last 7
// Days" queried 07/01 instead of the displayed 07/02).
const utcStartOfLocalDay = (d: Date) =>
  new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0, 0));
const utcEndOfLocalDay = (d: Date) =>
  new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59, 999));

export const getDateRangeBounds = (dateRange: string): DateRangeBounds => {
  const now = new Date();
  let startDate: Date;
  let endDate: Date;

  if (dateRange === 'All') {
    return {
      startDate: new Date(EPOCH_START),
      endDate: utcEndOfLocalDay(now),
    };
  }

  switch (dateRange) {
    case 'Today':
      startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0, 0);
      endDate = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);
      break;
    case 'Yesterday': {
      const yesterday = new Date(now);
      yesterday.setDate(now.getDate() - 1);
      startDate = new Date(
        yesterday.getFullYear(),
        yesterday.getMonth(),
        yesterday.getDate(),
        0,
        0,
        0,
        0
      );
      endDate = new Date(
        yesterday.getFullYear(),
        yesterday.getMonth(),
        yesterday.getDate(),
        23,
        59,
        59,
        999
      );
      break;
    }
    case 'Last 7 Days':
      startDate = new Date(now);
      startDate.setDate(now.getDate() - 6);
      startDate.setHours(0, 0, 0, 0);
      endDate = new Date(now);
      endDate.setHours(23, 59, 59, 999);
      break;
    case 'This Month':
      startDate = new Date(now.getFullYear(), now.getMonth(), 1, 0, 0, 0, 0);
      endDate = new Date(now);
      endDate.setHours(23, 59, 59, 999);
      break;
    case 'Last Month':
      startDate = new Date(now.getFullYear(), now.getMonth() - 1, 1, 0, 0, 0, 0);
      endDate = new Date(now.getFullYear(), now.getMonth(), 0, 23, 59, 59, 999);
      break;
    case 'Last 3 Months':
      startDate = new Date(now);
      startDate.setMonth(now.getMonth() - 3);
      startDate.setHours(0, 0, 0, 0);
      endDate = new Date(now);
      endDate.setHours(23, 59, 59, 999);
      break;
    case 'Last 6 Months':
      startDate = new Date(now);
      startDate.setMonth(now.getMonth() - 6);
      startDate.setHours(0, 0, 0, 0);
      endDate = new Date(now);
      endDate.setHours(23, 59, 59, 999);
      break;
    case 'This Year':
      startDate = new Date(now.getFullYear(), 0, 1, 0, 0, 0, 0);
      endDate = new Date(now);
      endDate.setHours(23, 59, 59, 999);
      break;
    case 'Last 30 Days':
    default:
      startDate = new Date(now);
      startDate.setDate(now.getDate() - 29);
      startDate.setHours(0, 0, 0, 0);
      endDate = new Date(now);
      endDate.setHours(23, 59, 59, 999);
      break;
  }

  // Handle custom date range format: "09/11/2025 - 09/12/2025"
  if (dateRange.includes(' - ')) {
    try {
      const [startDateStr, endDateStr] = dateRange.split(' - ');
      const parsedStart = new Date(startDateStr);
      const parsedEnd = new Date(endDateStr);
      if (!isNaN(parsedStart.getTime()) && !isNaN(parsedEnd.getTime())) {
        parsedStart.setHours(0, 0, 0, 0);
        parsedEnd.setHours(23, 59, 59, 999);
        startDate = parsedStart;
        endDate = parsedEnd;
      }
    } catch (error) {
      console.error('Error parsing custom date range:', error);
    }
  }

  // Pin the resolved local calendar day(s) to UTC so the query matches the label.
  return { startDate: utcStartOfLocalDay(startDate), endDate: utcEndOfLocalDay(endDate) };
};
